import { Suspense } from "react";
import { createLoader, loader, parallel, revalidate } from "@rangojs/router";
import { Link } from "@rangojs/router/client";
import { resolveImageUrl } from "../commerce/images.js";

function RecentlyViewedSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="animate-pulse">
          <div className="aspect-3/4 rounded bg-slate-200" />
          <div className="mt-2 h-4 w-3/4 rounded bg-slate-200" />
          <div className="mt-1 h-4 w-1/2 rounded bg-slate-200" />
        </div>
      ))}
    </div>
  );
}

function RecentlyViewedGrid({ products }: { products: any[] }) {
  const ctx = getRequestContext();
  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      <h2 className="mb-4 text-lg font-semibold text-slate-900">Recently Viewed</h2>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {products.map((p: any) => {
          const imageGroups = p.c_imageGroups ?? p.imageGroups ?? [];
          const img = (imageGroups.find((g: any) => g.viewType === "large") ?? imageGroups[0])
            ?.images?.[0];

          return (
            <Link
              key={p.id}
              prefetch="viewport"
              to={ctx.reverse("shop.product", { productId: p.id })}
              className="group"
            >
              {img && (
                <div className="aspect-3/4 overflow-hidden rounded bg-slate-100">
                  <img
                    src={resolveImageUrl(img.link)}
                    alt={img.alt ?? p.name ?? ""}
                    className="h-full w-full object-cover transition-transform group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
              )}
              <p className="mt-2 truncate text-sm font-medium text-slate-900">{p.name}</p>
              {p.price != null && (
                <p className="text-sm text-slate-600">
                  {p.currency ?? "GBP"} {p.price.toFixed(2)}
                </p>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

/** Loader — reads recently viewed IDs from session, streams product data */
export const RecentlyViewedLoader = createLoader(
  async (ctx) => {
    const session = ctx.get("session");
    if (!session) return { content: null };

    // Track the current product visit in session
    const currentProductId = ctx.params["productId"];
    const isPrefetch = ctx.request.headers.has("X-Rango-Prefetch");
    if (currentProductId && !isPrefetch) {
      const prev = session.get("recentlyViewed") ?? [];
      const updated = [currentProductId, ...prev.filter((id) => id !== currentProductId)].slice(
        0,
        10,
      );
      // Only mark the session dirty when the list actually changed — otherwise
      // re-viewing the already-most-recent product (reload / back-nav) writes an
      // identical list to KV on every request.
      const changed = updated.length !== prev.length || updated.some((id, i) => id !== prev[i]);
      if (changed) session.set("recentlyViewed", updated);
    }

    const viewed = session.get("recentlyViewed") ?? [];
    const ids = viewed.filter((id) => id !== currentProductId).slice(0, 4);
    if (ids.length === 0) return { content: null };

    const commerce = ctx.get("commerce")!;
    const productsPromise = commerce.shopperProducts
      .getProducts({ parameters: { ids } })
      .then((result) => {
        const products = result.data ?? [];
        if (products.length === 0) return null;
        return <RecentlyViewedGrid products={products} />;
      })
      .catch(() => null);

    return {
      content: productsPromise,
    };
  },
  { fetchable: true },
);

/** Attaches recently viewed parallel to the layout */
export const attachRecentlyViewed = () => [
  parallel(
    {
      "@recentlyViewed": () => (
        <Suspense fallback={<RecentlyViewedSkeleton />}>
          <RecentlyViewedSlot />
        </Suspense>
      ),
    },
    () => [loader(RecentlyViewedLoader, () => [revalidate(() => false)])],
  ),
];

import { RecentlyViewed as RecentlyViewedSlot } from "../components/RecentlyViewed.js";
import { getRequestContext } from "@rangojs/router";
